// src/app/console.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import {translation} from "../translation";

export interface Log {
  type: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: any[];
  timestamp: Date;
}

@Injectable()
export class ConsoleService {
  private logSource = new BehaviorSubject<Log[]>([]);
  private logs: Log[] = [];

  // Expose the logs as an observable for any component to display
  public logs$: Observable<Log[]> = this.logSource.asObservable();

  // Store the original console methods
  private originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
    debug: console.debug,
  };

  // Whether to forward to the original console (default true for backward compatibility)
  private forwardToOriginal: boolean = true;

  constructor() {
    if (translation?.debug) {
      this.forwardToOriginal = true;
    }
  }

  // Call this method to start overriding the console
  public startCapturing(): void {
    console.log = (...args: any[]) => {
      this.addLog('log', args);
      if (this.forwardToOriginal) {
        this.originalConsole.log(...args);
      }
    };

    console.warn = (...args: any[]) => {
      this.addLog('warn', args);
      if (this.forwardToOriginal) {
        this.originalConsole.warn(...args);
      }
    };

    console.error = (...args: any[]) => {
      this.addLog('error', args);
      if (this.forwardToOriginal) {
        this.originalConsole.error(...args);
      }
    };

    console.info = (...args: any[]) => {
      this.addLog('info', args);
      if (this.forwardToOriginal) {
        this.originalConsole.info(...args);
      }
    };

    console.debug = (...args: any[]) => {
      this.addLog('debug', args);
      if (this.forwardToOriginal) {
        this.originalConsole.debug(...args);
      }
    };
  }

  // Call this method to restore the original console functionality
  public stopCapturing(): void {
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;
  }

  private addLog(type: Log['type'], message: any[]): void {
    const newLog: Log = {
      type: type,
      message: message,
      timestamp: new Date()
    };
    this.logs = [...this.logs, newLog];
    this.logSource.next(this.logs);
  }

  public clearLogs(): void {
    this.logs = [];
    this.logSource.next(this.logs);
  }
}
